import { useEffect, useRef } from 'react';

import { useNamedLogger } from '@eluve/logger';

type TabState = 'active' | 'passive' | 'hidden' | 'frozen' | 'terminated';

const Options = { capture: true };
const SupportsFreeze = 'onfreeze' in document;
const SupportsResume = 'onresume' in document;

const getState = () => {
  if (document.visibilityState === 'hidden') {
    return 'hidden';
  }
  return document.hasFocus() ? 'active' : 'passive';
};

export const useTabLifecycleReporting = () => {
  const logger = useNamedLogger('TabLifecycleReporting');
  const currentState = useRef<TabState>(getState());

  useEffect(() => {
    const logStateChange = (nextState: TabState) => {
      const previousState = currentState.current;
      if (nextState !== previousState) {
        currentState.current = nextState;
        logger.info(`Tab became ${nextState}`, { previousState });
      }
    };

    // These events can all call the `getState()` function to determine the next state.
    const onChange = () => {
      logStateChange(getState());
    };

    // On the freeze event the next state is always frozen.
    const onFreeze = () => {
      logStateChange('frozen');
    };

    // On page hide state is either frozen (if entering back/forward cache) or terminated (if unloading).
    const onPageHide = (event: PageTransitionEvent) => {
      logStateChange(event.persisted ? 'frozen' : 'terminated');
    };

    window.addEventListener('blur', onChange, Options);
    window.addEventListener('focus', onChange, Options);
    window.addEventListener('pagehide', onPageHide, Options);
    window.addEventListener('pageshow', onChange, Options);
    window.addEventListener('visibilitychange', onChange, Options);

    if (SupportsFreeze) {
      window.addEventListener('freeze', onFreeze, Options);
    }
    if (SupportsResume) {
      window.addEventListener('resume', onChange, Options);
    }

    return () => {
      window.removeEventListener('blur', onChange, Options);
      window.removeEventListener('focus', onChange, Options);
      window.removeEventListener('pagehide', onPageHide, Options);
      window.removeEventListener('pageshow', onChange, Options);
      window.removeEventListener('visibilitychange', onChange, Options);

      if (SupportsFreeze) {
        window.removeEventListener('freeze', onFreeze, Options);
      }
      if (SupportsResume) {
        window.removeEventListener('resume', onChange, Options);
      }
    };
  }, [logger]);
};
